import React, { useCallback, useEffect, useState } from 'react';

interface SidebarItem {
  id: string;
  label: string;
  component: React.ReactNode;
}

interface SidebarLayoutProps {
  sections: SidebarItem[];
  activeSectionId?: string;
  title?: string;
  header?: React.ReactNode;
  onlyShowActiveSection?: boolean;
  onSectionClick?: (id: string) => void;
}

export const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  sections,
  activeSectionId: activeSectionIdProp,
  title,
  header,
  onlyShowActiveSection = false,
  onSectionClick,
}) => {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(activeSectionIdProp);

  useEffect(() => {
    setActiveSectionId(activeSectionIdProp);
  }, [activeSectionIdProp]);

  const handleSectionClick = useCallback((id: string, e: React.MouseEvent) => {
    e.preventDefault();
    setActiveSectionId(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    onSectionClick?.(id);
  }, [onSectionClick]);

  const activeSectionComponent = sections.find((section) => section.id === activeSectionId)?.component;

  return (
    <div className="flex h-full">
      {/* Left Sidebar */}
      <div className="w-48 border-r border-gray-200 p-4">
        {title && <h2 className="text-lg font-medium mb-4">{title}</h2>}
        <nav className="space-y-1">
          {sections.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={(e) => handleSectionClick(id, e)}
              className={`block px-3 py-2 rounded-md text-sm font-medium ${
                activeSectionId === id
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              {label}
            </a>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-[800px] mx-auto space-y-6">
          {header}
          {onlyShowActiveSection ? activeSectionComponent : sections.map(({ id, component }) => (
            <div key={id} id={id} className="scroll-mt-6">
              {component}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
